import React from "react";

import { FormControl, FormHelperText } from "@mui/material";

import styled from "styled-components";

import { FieldProps, useFormFull } from "form-full";
import { Spacing } from "../../config";
import FontStyles from "../styles/fontStyles";

import PhoneInput from "react-phone-number-input";

const Label = styled.p<{ $withError?: boolean; disabled?: boolean }>(({ theme, $withError }) => {
  const { palette: colors } = theme as any;
  return {
    ...FontStyles.semibold14,
    textTransform: "uppercase",
    padding: 0,
    margin: 0,
    color: $withError ? colors.error.main : colors.action.selected,
    transition: ".2s",
    pointerEvents: "none",
    alignItems: "center",
    display: "flex",
    overflow: "hidden",
    marginTop: 0,
    marginBottom: Spacing(1),
  };
});
const LabelView = styled.div(() => {
  return {
    display: "flex",
    alignContent: "center",
    justifyContent: "flex-start",
  };
});

const StyledInput = styled(PhoneInput)<{
  $inputStyle?: any;
  $error?: boolean;
  $minHeight?: boolean;
  readOnly?: boolean;
  error?: boolean;
  disabled?: boolean;
}>(
  ({
    theme,
    error,
    disabled,
    $inputStyle,
    $minHeight,
    readOnly,
  }) => {
    const themeTyped = theme as any;
    return {
      ...FontStyles.medium14,
      padding: themeTyped.spacing(1.962, 2.5),
      alignItems: $minHeight ? "flex-start" : undefined,
      paddingTop: $minHeight ? Spacing(2.5) : undefined,
      marginTop: Spacing(0.5),
      minHeight: $minHeight ? Spacing(20) : undefined,
      textOverflow: "ellipsis",
      color: error ? "red" : readOnly ? "#00000099" : "black",
      backgroundColor: themeTyped.palette.primary.contrastText,
      borderBottom: "0px",
      borderRadius: themeTyped.spacing(0.6),
      opacity: disabled ? 0.6 : 1,
      display: "flex",
      width: "100%",

      "& .PhoneInputCountry": {
        position: "relative",
        display: "flex",
        alignItems: "center",
      },
      "& .PhoneInputCountrySelect": {
        position: "absolute",
        top: 0,
        left: 0,
        height: "100%",
        width: "100%",
        zIndex: 1,
        border: 0,
        opacity: 0,
        cursor: "pointer",
      },

      "& .PhoneInputCountryIcon": {
        width: "28px",
        display: "flex",
      },
      "& .PhoneInputCountrySelectArrow": {
        marginRight: "8px",
        marginLeft: "8px",
        marginTop: "-4px",
        display: "block",
        content: "''",
        width: "11px",
        height: "11px",

        borderStyle: "solid",
        borderColor: "var(--PhoneInputCountrySelectArrow-color, currentColor)",
        borderTopWidth: 0,
        borderBottomWidth:
          "var(--PhoneInputCountrySelectArrow-borderWidth, 1px)",
        borderLeftWidth: 0,
        borderRightWidth:
          "var(--PhoneInputCountrySelectArrow-borderWidth, 1px)",
        transform:
          "var(--PhoneInputCountrySelectArrow-transform, rotate(45deg))",
        opacity: "var(--PhoneInputCountrySelectArrow-opacity, 0.45)",
      },

      "& .PhoneInputInput": {
        ...FontStyles.medium14,
        border: "none",
        textOverflow: "ellipsis",
        color: error ? "red" : readOnly ? "#00000099" : "black",
        backgroundColor: themeTyped.palette.primary.contrastText,
        display: "flex",
        width: "100%",
        "&:focus-visible": {
          outline: "none",
        },
      },

      "& .PhoneInputInput:focus-visible": {
        outline: "none",
      },

      ...$inputStyle,
    };
  }
);

const ErrorMessage = styled(FormHelperText)(() => {
  return {
    color: "red",
    minHeight: Spacing(2.5),
  };
});

function getErrorClassname(base: string, error: any, valid: any) {
  const className = base;
  if (error) return `${className} invalid`;
  if (valid) return `${className} valid`;
  return className;
}

function getHint(error: any) {
  if (error) return error;

  return null;
}
export interface Props extends FieldProps<any> {
  id?: string;
  label?: string;
  disableError?: boolean;
  hideVisualError?: boolean;
  type?: string;
  withHide?: () => void;
  placeholder?: string;
  state?: boolean;
  readOnly?: boolean;
  maxLength?: number;
  endButton?: (onChange: (event: any, value: any) => void, value: any) => void;

  multiline?: boolean;
  name: string;
  customBlur?: any;
  disabled?: boolean;
  isBrasil?: boolean;
}

function InputPhone(props: Props) {
  const { value, error, ref, valid, onChange, ffHandler } =
    useFormFull.field(props);

  const {
    id,
    label,
    required,
    disableError,
    hideVisualError,

    placeholder,

    readOnly,

    multiline,
    name,
    customBlur,
    disabled,
    isBrasil = true,
  } = props;

  React.useEffect(() => {
    if (label) {
      const test = document.querySelector(".form-input-" + name)?.children;
      if (test) {
        const a = Array.from(test as HTMLCollectionOf<HTMLElement>).find(
          (element: any) => {
            if (element?.localName === "fieldset") {
              return element;
            } else {
              return null;
            }
          }
        );

        if (a) {
          a.style.paddingLeft = Spacing(3) + "px";
        }
      }
    }
  }, [label, name]);

  return (
    <FormControl
      className={getErrorClassname("form-control", error, valid)}
      fullWidth
      error={hideVisualError ? undefined : Boolean(error)}
    >
      {label && (
        <LabelView>
          <Label
            disabled={disabled}
            className={"form-input-label-" + name}
            $withError={Boolean(error)}
          >
            {label}
          </Label>
        </LabelView>
      )}
      <StyledInput
        numberInputProps={{
          className: "focus:outline-none focus:ring-0",
        }}
        defaultCountry={isBrasil ? "BR" : undefined}
        international={false}
        disabled={disabled}
        $minHeight={multiline}
        error={!!error}
        id={id}
        name={name}
        value={value}
        ref={ref as any}
        required={!!required}
        placeholder={placeholder}
        onChange={(value: any) => {
          onChange(null, value);
        }}
        onBlur={async () => {
          const hasError = await ffHandler?.testFieldError(name, true);

          if (!hasError && customBlur) {
            let inputValue = ffHandler?.getValue(name, true);
            customBlur(inputValue);
          }
        }}
        readOnly={readOnly}
        className={"form-input-" + label?.replace(/\s/g, "")}
      />

      {disableError ? null : (
        <ErrorMessage error={Boolean(error)}>{getHint(error)}</ErrorMessage>
      )}
    </FormControl>
  );
}

export default InputPhone;
